import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowUpDown, Banknote, Camera, Check, HandCoins, LayoutGrid, List, MapPinned, Pencil, Plus, ShieldCheck, Trash2, X } from "lucide-react";
import { PeriodFilter } from "@/components/PeriodFilter";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, Field, PageHeader } from "@/components/common";
import { MemberSearch } from "@/components/MemberSearch";
import { MoneyInput } from "@/components/MoneyInput";
import { CameraCapture } from "@/components/CameraCapture";
import { CollateralDialog } from "@/components/CollateralDialog";
import { LoanDetail } from "@/components/LoanDetail";
import { LoanTable } from "@/components/LoanTable";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AuthImage } from "@/components/AuthImage";
import { SignaturePad } from "@/components/SignaturePad";
import { sisaOf } from "@/components/LoanTable";
import { DatePicker } from "@/components/TxTable";
import { FREQ_LABELS, FREQ_UNIT, LOAN_STATUS, RATES, rupiah, todayStr } from "@/lib/format";
import { usePoll } from "@/lib/live";

const INIT = { principal: "", tenor: "30", frequency: "harian", purpose: "", loan_date: todayStr() };

const Simulation = ({ sim, f }) => (
  <div className="rounded-2xl bg-muted/50 p-4 grid grid-cols-2 gap-3 text-sm" data-testid="loan-simulation">
    <div><p className="eyebrow">Angsuran</p><p className="font-mono font-bold text-lg text-primary" data-testid="sim-installment">{rupiah(sim.installment_amount)}</p><p className="text-xs text-muted-foreground">{sim.installment_count}x {FREQ_LABELS[f.frequency].toLowerCase()}</p></div>
    <div><p className="eyebrow">Total Bayar</p><p className="font-mono font-bold text-lg" data-testid="sim-total">{rupiah(sim.total_payable)}</p><p className="text-xs text-muted-foreground">Margin {RATES.margin}% {rupiah(sim.interest_total)}</p></div>
    <div className="col-span-2 border-t pt-3 space-y-1 text-xs">
      <p className="flex justify-between"><span className="text-muted-foreground">Biaya administrasi ({RATES.admin}%)</span><span className="font-mono" data-testid="sim-admin">- {rupiah(sim.admin_fee)}</span></p>
      <p className="flex justify-between"><span className="text-muted-foreground">Masuk simpanan anggota ({RATES.savings}%)</span><span className="font-mono" data-testid="sim-savings">- {rupiah(sim.savings_cut)}</span></p>
      <p className="flex justify-between text-sm font-semibold"><span>Diterima anggota</span><span className="font-mono text-primary" data-testid="sim-net">{rupiah(sim.net_disbursed)}</span></p>
    </div>
  </div>
);

const LoanForm = ({ open, onOpenChange, onSaved, user }) => {
  const [member, setMember] = useState(null);
  const [f, setF] = useState(INIT);
  const [sim, setSim] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  useEffect(() => { if (!open) { setMember(null); setSim(null); setF({ ...INIT, loan_date: todayStr() }); } }, [open]);
  const body = member && { nik: member.nik, principal: Number(f.principal), tenor: Number(f.tenor), frequency: f.frequency, purpose: f.purpose, loan_date: f.loan_date };
  useEffect(() => {
    if (!body || !body.principal || !body.tenor) { setSim(null); return undefined; }
    const t = setTimeout(() => api.post("/loans/simulate", body).then((r) => setSim(r.data)).catch(() => setSim(null)), 250);
    return () => clearTimeout(t);
  }, [member, f]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = (e) => {
    e.preventDefault();
    if (!body?.principal) { toast.error("Pilih anggota dan isi nominal pinjaman"); return; }
    setConfirm(true);
  };
  const doSubmit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/loans", body); toast.success(`Pengajuan ${data.loan_no} dibuat`); onOpenChange(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const resort = member?.resort_name;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="loan-form-dialog">
        <DialogHeader><DialogTitle className="font-heading">Pengajuan Pinjaman</DialogTitle><DialogDescription>Margin flat {RATES.margin}% dari nilai pinjaman · potongan {RATES.admin}% admin & {RATES.savings}% simpanan.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Tanggal Pinjaman"><DatePicker value={f.loan_date} onChange={(v) => setF({ ...f, loan_date: v })} testId="loan-date-input" /></Field>
          <MemberSearch value={member} onSelect={setMember} testId="loan-member" />
          {resort && <p className="text-xs flex items-center gap-1.5 text-primary font-medium" data-testid="loan-form-resort"><MapPinned className="h-3.5 w-3.5" />Resort {resort}</p>}
          <Field label="Nominal Pinjaman"><MoneyInput value={f.principal} onChange={(v) => setF({ ...f, principal: v })} className="h-12 text-lg" testId="loan-principal-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frekuensi Angsuran">
              <Select value={f.frequency} onValueChange={(v) => setF({ ...f, frequency: v })}>
                <SelectTrigger className="h-11" data-testid="loan-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FREQ_LABELS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`loan-frequency-${k}`}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label={`Tenor (${FREQ_UNIT[f.frequency]})`}><Input type="number" min="1" max="400" className="h-11" value={f.tenor} onChange={(e) => setF({ ...f, tenor: e.target.value })} data-testid="loan-tenor-input" /></Field>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="loan-flow-hint">{f.frequency === "harian" ? "Harian: disetujui & dicairkan langsung oleh Petugas Lapangan (memotong modal petugas)." : "Non-harian: disetujui Kepala Bagian, dicairkan Kasir dengan jenis & bukti jaminan."}</p>
          <Field label="Keperluan (opsional)"><Input className="h-11" value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} data-testid="loan-purpose-input" /></Field>
          {sim && <Simulation sim={sim} f={f} />}
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full h-11 px-6" data-testid="loan-form-submit">{busy ? "Menyimpan..." : "Ajukan Pinjaman"}</Button></DialogFooter>
        </form>
        <ConfirmDialog open={confirm} onOpenChange={setConfirm} title="Konfirmasi Pengajuan Pinjaman" onConfirm={doSubmit} confirmLabel="Ya, ajukan"
          description={member && <>Ajukan pinjaman <b>{rupiah(f.principal)}</b> ({f.tenor}x {FREQ_LABELS[f.frequency].toLowerCase()}) untuk <b>{member.name}</b>?</>} />
      </DialogContent>
    </Dialog>
  );
};

const perms = (loan, user) => {
  const harian = loan.frequency === "harian";
  const own = loan.created_by === user.id;
  const isPetugas = user.role === "petugas" && harian && own;
  return {
    approve: loan.status === "diajukan" && (user.role === "kepala_bagian" || isPetugas),
    disburse: loan.status === "disetujui" && (user.role === "kasir" || isPetugas),
    collateral: loan.status === "disetujui" && !harian && user.role === "kasir",
  };
};

const LoanActions = ({ loan, user, onApprove, onPhoto, onCollateral, onDisburse, busy }) => {
  const p = perms(loan, user);
  const harian = loan.frequency === "harian";
  if (p.approve) return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" className="h-10 rounded-full flex-1 text-rose-600" disabled={busy} onClick={() => onApprove(loan, false)} data-testid={`loan-reject-${loan.id}`}><X className="h-4 w-4 mr-1" />Tolak</Button>
      <Button size="sm" className="h-10 rounded-full flex-1" disabled={busy} onClick={() => onApprove(loan, true)} data-testid={`loan-approve-${loan.id}`}><Check className="h-4 w-4 mr-1" />Setujui</Button>
    </div>
  );
  if (p.disburse) {
    const ready = loan.photo_path && (harian || loan.collateral_path);
    return (
      <div className="space-y-2">
        <div className="flex gap-2">
          <Button size="sm" variant={loan.photo_path ? "outline" : "default"} className="h-10 rounded-full flex-1" onClick={() => onPhoto(loan)} data-testid={`disburse-loan-photo-btn-${loan.id}`}><Camera className="h-4 w-4 mr-1" />{loan.photo_path ? "Foto Ulang" : "Foto Anggota"}</Button>
          {p.collateral && <Button size="sm" variant={loan.collateral_path ? "outline" : "default"} className="h-10 rounded-full flex-1" onClick={() => onCollateral(loan)} data-testid={`loan-collateral-btn-${loan.id}`}><ShieldCheck className="h-4 w-4 mr-1" />Jaminan</Button>}
        </div>
        <Button size="sm" className="h-10 rounded-full w-full" disabled={!ready || busy} onClick={() => onDisburse(loan)} data-testid={`loan-disburse-${loan.id}`}><Banknote className="h-4 w-4 mr-1" />Cairkan {rupiah(loan.net_disbursed ?? loan.principal)}</Button>
      </div>
    );
  }
  if (loan.status === "disetujui") return <p className="text-xs text-muted-foreground">{harian ? "Menunggu foto & pencairan oleh Petugas Lapangan." : "Menunggu foto, jaminan & pencairan oleh Kasir."}</p>;
  if (loan.status === "diajukan") return <p className="text-xs text-muted-foreground">{harian ? "Menunggu persetujuan Petugas Lapangan." : "Menunggu persetujuan Kepala Bagian."}</p>;
  return null;
};

const STATUS_TABS = ["diajukan", "disetujui", "berjalan", "lunas", "ditolak", "semua"];

const LoanCards = ({ items, onOpen }) => (
  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="loans-cards">
    {items.map((l) => (
      <button type="button" key={l.id} onClick={() => onOpen(l)} className="text-left rounded-3xl border bg-card p-4 shadow-sm hover:border-primary transition-colors" data-testid={`loan-card-${l.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0"><p className="font-heading font-semibold truncate">{l.member_name}</p><p className="font-mono text-xs text-muted-foreground">{l.nik}</p></div>
          <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold shrink-0 ${LOAN_STATUS[l.status].cls}`}>{LOAN_STATUS[l.status].label}</span>
        </div>
        <div className="mt-3 flex items-end justify-between gap-2">
          <div><p className="text-[11px] text-muted-foreground">No. Pinjaman</p><p className="font-mono text-sm">{l.loan_no || "-"}</p></div>
          <div className="text-right"><p className="text-[11px] text-muted-foreground">Sisa Pinjaman</p><p className="font-mono font-bold text-primary" data-testid={`loan-card-sisa-${l.id}`}>{sisaOf(l) === null ? "-" : rupiah(sisaOf(l))}</p></div>
        </div>
      </button>
    ))}
  </div>
);

const FlowInfo = ({ l }) => (
  <div className="rounded-2xl bg-muted/50 p-4 grid grid-cols-2 gap-3 text-sm" data-testid="loan-flow-info">
    <div><p className="eyebrow">Anggota</p><p className="font-semibold">{l.member_name}</p><p className="font-mono text-xs text-muted-foreground">{l.nik}</p></div>
    <div><p className="eyebrow">Resort</p><p className="font-semibold">{l.resort_name || "-"}</p></div>
    <div><p className="eyebrow">Pokok</p><p className="font-mono font-bold">{rupiah(l.principal)}</p></div>
    <div><p className="eyebrow">Diterima</p><p className="font-mono font-bold text-primary">{rupiah(l.net_disbursed ?? l.principal)}</p></div>
    <div className="col-span-2 text-xs text-muted-foreground">{l.tenor}x {FREQ_LABELS[l.frequency].toLowerCase()} · angsuran {rupiah(l.installment_amount)}</div>
  </div>
);

const SignStep = ({ loan, busy, onSign }) => {
  const [redo, setRedo] = useState(false);
  useEffect(() => { setRedo(false); }, [loan.signature_path]);
  if (loan.signature_path && !redo) return (
    <div className="space-y-2" data-testid="loan-flow-signed">
      <AuthImage path={`/loans/${loan.id}/signature?v=${encodeURIComponent(loan.signature_path)}`} alt="Tanda tangan" className="w-full h-32 rounded-2xl object-contain bg-white border" testId="loan-flow-signature" />
      <Button type="button" variant="ghost" size="sm" className="rounded-full w-full" onClick={() => setRedo(true)} data-testid="loan-flow-resign">Ulangi tanda tangan</Button>
    </div>
  );
  return <SignaturePad busy={busy} onSave={(b) => onSign(loan, b)} />;
};

const LoanFlowDialog = ({ loan, user, busy, onClose, onApprove, onSign, onPhoto, onCollateral, onDisburse }) => {
  const p = loan ? perms(loan, user) : {};
  const ready = loan?.photo_path && (loan.frequency === "harian" || loan.collateral_path);
  return (
    <Dialog open={Boolean(loan)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="loan-flow-dialog">
        {loan && <>
          <DialogHeader>
            <DialogTitle className="font-heading">{loan.status === "diajukan" ? "Persetujuan Pinjaman" : "Pencairan Pinjaman"}</DialogTitle>
            <DialogDescription>{loan.loan_no || ""} · {LOAN_STATUS[loan.status].label}</DialogDescription>
          </DialogHeader>
          <FlowInfo l={loan} />
          {p.approve && <SignStep loan={loan} busy={busy} onSign={onSign} />}
          {p.approve && (
            <div className="flex gap-2">
              <Button variant="outline" className="h-12 rounded-full flex-1 text-rose-600" disabled={busy} onClick={() => onApprove(loan, false)} data-testid="loan-flow-reject"><X className="h-4 w-4 mr-1" />Tolak</Button>
              <Button className="h-12 rounded-full flex-1" disabled={busy || !loan.signature_path} onClick={() => onApprove(loan, true)} data-testid="loan-flow-approve"><Check className="h-4 w-4 mr-1" />Setujui</Button>
            </div>
          )}
          {p.disburse && (
            <div className="space-y-3">
              {loan.photo_path
                ? <AuthImage path={`/loans/${loan.id}/photo?v=${encodeURIComponent(loan.photo_path)}`} alt="Foto pencairan" className="w-full aspect-[4/3] rounded-2xl object-cover" testId="loan-flow-photo" />
                : <div className="w-full aspect-[4/3] rounded-2xl border-2 border-dashed grid place-items-center text-sm text-muted-foreground" data-testid="loan-flow-photo-empty">Belum ada foto pencairan</div>}
              <div className="flex gap-2">
                <Button variant={loan.photo_path ? "outline" : "default"} className="h-11 rounded-full flex-1" onClick={() => onPhoto(loan)} data-testid="loan-flow-photo-btn"><Camera className="h-4 w-4 mr-1" />{loan.photo_path ? "Foto Ulang" : "Foto Anggota"}</Button>
                {p.collateral && <Button variant={loan.collateral_path ? "outline" : "default"} className="h-11 rounded-full flex-1" onClick={() => onCollateral(loan)} data-testid="loan-flow-collateral"><ShieldCheck className="h-4 w-4 mr-1" />Jaminan</Button>}
              </div>
              <Button className="h-12 rounded-full w-full" disabled={!ready || busy} onClick={() => onDisburse(loan)} data-testid="loan-flow-disburse"><Banknote className="h-4 w-4 mr-1" />Cairkan Pinjaman {rupiah(loan.net_disbursed ?? loan.principal)}</Button>
              {!ready && <p className="text-xs text-muted-foreground text-center">{loan.photo_path ? "Lengkapi jaminan untuk mengaktifkan pencairan." : "Ambil foto anggota untuk mengaktifkan pencairan."}</p>}
            </div>
          )}
          {!p.approve && !p.disburse && <p className="text-sm text-muted-foreground text-center" data-testid="loan-flow-wait">{loan.status === "diajukan" ? "Menunggu persetujuan oleh petugas yang berwenang." : "Menunggu pencairan oleh petugas yang berwenang."}</p>}
        </>}
      </DialogContent>
    </Dialog>
  );
};

export default function LoansPage() {
  const { user } = useAuth();
  const isPetugas = user.role === "petugas";
  const [status, setStatus] = useState(isPetugas ? "semua" : "diajukan");
  const [items, setItems] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [photoLoan, setPhotoLoan] = useState(null);
  const [colLoan, setColLoan] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [flowId, setFlowId] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("ksp_loan_view") || "tabel");
  const pickView = (v) => { setView(v); localStorage.setItem("ksp_loan_view", v); };
  const openLoan = (l) => (["diajukan", "disetujui"].includes(l.status) ? setFlowId(l.id) : setDetailId(l.id));
  const flow = flowId && items ? items.find((l) => l.id === flowId && ["diajukan", "disetujui"].includes(l.status)) : null;
  const canCreate = ["kasir", "petugas"].includes(user.role);

  const reqId = useRef(0);
  const [range, setRange] = useState(null);
  const [asc, setAsc] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const canEdit = ["admin_cabang", "kepala_bagian", "kasir"].includes(user.role);
  const load = useCallback(() => {
    if (!range) return;
    const id = ++reqId.current;
    api.get("/loans", { params: { ...range, ...(status === "semua" ? {} : { status }) } })
      .then((r) => { if (id === reqId.current) setItems(r.data); }).catch((e) => toast.error(errMsg(e)));
  }, [status, range]);
  useEffect(() => { setItems(null); load(); }, [load]);
  usePoll(load);

  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); load(); return true; } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); } };
  const onApprove = (l, approve) => act(() => api.post(`/loans/${l.id}/approve`, { approve }), approve ? "Pinjaman disetujui" : "Pinjaman ditolak").then((ok) => ok && setFlowId(null));
  const onDisburse = (l) => act(() => api.post(`/loans/${l.id}/disburse`), `Pinjaman dicairkan ${rupiah(l.net_disbursed ?? l.principal)} — status berjalan`).then((ok) => ok && setFlowId(null));
  const onSign = (l, blob) => {
    const fd = new FormData();
    fd.append("file", blob, "ttd.png");
    return act(() => api.post(`/loans/${l.id}/signature`, fd), "Tanda tangan tersimpan");
  };
  const onPhoto = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "foto.jpg");
    if (window.__gps) { fd.append("lat", window.__gps.lat); fd.append("lng", window.__gps.lng); }
    await act(() => api.post(`/loans/${photoLoan.id}/photo`, fd), "Foto anggota tersimpan");
    setPhotoLoan(null);
  };

  return (
    <div>
      <PageHeader title="Pinjaman"
        action={canCreate && <Button className="rounded-full h-11 px-6" onClick={() => setFormOpen(true)} data-testid="add-loan-button"><Plus className="h-4 w-4 mr-2" />Ajukan Pinjaman</Button>} />
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <PeriodFilter onChange={setRange} initial="bulanan" testId="loan-period" />
        <Button variant="outline" className="h-11 rounded-full" onClick={() => setAsc(!asc)} data-testid="loan-sort-date"><ArrowUpDown className="h-4 w-4 mr-2" />Tanggal {asc ? "terlama" : "terbaru"}</Button>
        <div className="inline-flex rounded-full border p-1" data-testid="loan-view-toggle">
          <Button size="sm" variant={view === "kartu" ? "default" : "ghost"} className="h-9 rounded-full" onClick={() => pickView("kartu")} data-testid="loan-view-kartu"><LayoutGrid className="h-4 w-4 mr-1" />Kartu</Button>
          <Button size="sm" variant={view === "tabel" ? "default" : "ghost"} className="h-9 rounded-full" onClick={() => pickView("tabel")} data-testid="loan-view-tabel"><List className="h-4 w-4 mr-1" />Tabel</Button>
        </div>
      </div>
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList className="flex-wrap h-auto">
          {(isPetugas ? ["semua", ...STATUS_TABS.slice(0, -1)] : STATUS_TABS).map((s) => (
            <TabsTrigger key={s} value={s} className="h-9" data-testid={`loan-tab-${s}`}>{s === "semua" ? "Semua" : LOAN_STATUS[s].label}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {items === null ? <p className="text-muted-foreground">Memuat...</p> : items.length === 0 ? (
        <EmptyState icon={HandCoins} title="Tidak ada pinjaman" text="Belum ada pinjaman dengan status ini." testId="loans-empty" />
      ) : (() => {
        const key = (l) => `${l.loan_date || (l.created_at || "").slice(0, 10)}|${l.created_at || ""}`;
        const sorted = [...items].sort((a, b) => (key(a) < key(b) ? -1 : 1) * (asc ? 1 : -1));
        const groups = isPetugas ? Object.entries(sorted.reduce((g, l) => { (g[l.resort_name || "Tanpa Resort"] ||= []).push(l); return g; }, {})).sort(([a], [b]) => a.localeCompare(b)) : [[null, sorted]];
        return <div className="space-y-6">{groups.map(([gname, rows]) => (
          <div key={gname || "all"} data-testid={`loan-group-${gname || "all"}`}>
            {gname && <p className="font-heading font-semibold mb-2">{gname} <span className="text-xs font-normal text-muted-foreground">{rows.length} pinjaman</span></p>}
        {view === "kartu" ? <LoanCards items={rows} onOpen={openLoan} /> : <LoanTable items={rows} tenorOnly={isPetugas} onDetail={setDetailId} onMember={(l) => setFlowId(l.id)} Actions={({ loan }) => (
          <div className="space-y-2">
            <LoanActions loan={loan} user={user} busy={busy} onApprove={(l, a) => (a ? setFlowId(l.id) : onApprove(l, false))} onPhoto={setPhotoLoan} onCollateral={setColLoan} onDisburse={onDisburse} />
            <div className="flex gap-1">
              {canEdit && <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => setEdit({ ...loan, kol: String(loan.kol || 1), loan_date: loan.loan_date || loan.created_at.slice(0, 10) })} data-testid={`loan-edit-${loan.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>}
              {user.role === "admin_cabang" && !["berjalan", "lunas"].includes(loan.status) && <Button size="sm" variant="ghost" className="h-8 rounded-full text-rose-600" onClick={() => setDel(loan)} data-testid={`loan-delete-${loan.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Hapus</Button>}
            </div>
          </div>
        )} />}
          </div>
        ))}</div>;
      })()}
      <LoanForm open={formOpen} onOpenChange={setFormOpen} user={user} onSaved={() => { setStatus(isPetugas ? "semua" : "diajukan"); load(); }} />
      <LoanFlowDialog loan={flow} user={user} busy={busy} onClose={() => setFlowId(null)} onApprove={onApprove} onSign={onSign} onPhoto={setPhotoLoan} onCollateral={setColLoan} onDisburse={onDisburse} />
      <CameraCapture open={Boolean(photoLoan)} onOpenChange={(o) => !o && setPhotoLoan(null)} onConfirm={onPhoto} busy={busy} stamp initialFacing="environment" lockFacing />
      <CollateralDialog loan={colLoan} onClose={() => setColLoan(null)} onSaved={load} />
      <LoanDetail id={detailId} onClose={() => setDetailId(null)} />
      <Dialog open={Boolean(edit)} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="loan-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading">Edit Pinjaman {edit?.loan_no || ""}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-4">
            <Field label="Tanggal Pinjaman"><DatePicker value={edit.loan_date} onChange={(v) => setEdit({ ...edit, loan_date: v })} testId="loan-edit-date" /></Field>
            <Field label="Kolektibilitas (Kol)">
              <Select value={edit.kol} onValueChange={(v) => setEdit({ ...edit, kol: v })}>
                <SelectTrigger className="h-11" data-testid="loan-edit-kol"><SelectValue /></SelectTrigger>
                <SelectContent>{["1 · Lancar", "2 · Dalam Perhatian Khusus", "3 · Kurang Lancar", "4 · Diragukan", "5 · Macet"].map((t, i) => <SelectItem key={t} value={String(i + 1)}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <DialogFooter><Button className="rounded-full h-11 px-6" disabled={busy} data-testid="loan-edit-submit" onClick={() => act(() => api.put(`/loans/${edit.id}`, { loan_date: edit.loan_date, kol: Number(edit.kol) }), "Pinjaman diperbarui").then(() => setEdit(null))}>Simpan</Button></DialogFooter>
          </div>}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={Boolean(del)} onOpenChange={(o) => !o && setDel(null)} title="Hapus Pinjaman" danger confirmLabel="Ya, hapus"
        onConfirm={() => act(() => api.delete(`/loans/${del.id}`), "Pinjaman dihapus")} description={del && <>Hapus pinjaman <b>{del.loan_no || ""}</b> milik <b>{del.member_name}</b>?</>} />
    </div>
  );
}
